import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper, Chip, TextField, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import { useSearchParams } from 'react-router-dom';
import { listMacieBuckets } from '../../../services/macieApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx, statusChipSx,
} from '../portalStyles';

const MACIE_COLOR = '#0d9488';

const PUBLIC_OPTIONS = ['PUBLIC', 'NOT_PUBLIC', 'UNKNOWN'];
const SHARED_OPTIONS = ['EXTERNAL', 'INTERNAL', 'NOT_SHARED', 'UNKNOWN'];
// macie2 serverSideEncryption.type values.
const ENCRYPTION_OPTIONS = ['NONE', 'AES256', 'aws:kms', 'aws:kms:dsse'];

// human-readable byte size (1024-based).
const formatBytes = (n) => {
  const bytes = Number(n) || 0;
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const val = bytes / (1024 ** i);
  return `${val >= 100 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
};

const publicIntent = (v) => (v === 'PUBLIC' ? 'error' : v === 'NOT_PUBLIC' ? 'success' : 'neutral');
const sharedIntent = (v) => (v === 'EXTERNAL' ? 'warning' : v === 'INTERNAL' ? 'info' : 'neutral');
const encIntent = (v) => (!v || v === 'NONE' ? 'warning' : 'success');
const prettyEnc = (v) => (!v || v === 'NONE' ? 'None' : v);

export default function MacieBuckets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters — seeded from the URL so dashboard posture cards deep-link here.
  const [account, setAccount] = useState(searchParams.get('account') || '');
  const [publicAccess, setPublicAccess] = useState(searchParams.get('public') || '');
  const [shared, setShared] = useState(searchParams.get('shared') || '');
  const [encryption, setEncryption] = useState(searchParams.get('encryption') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    (async () => {
      try {
        const res = await listSecurityHubAccounts();
        setAccounts(res?.data?.accounts || res?.data || []);
      } catch (_) { /* the account filter degrades to id-only labels */ }
    })();
  }, []);

  // Monotonic request guard — the debounce suppresses keystroke storms but does
  // NOT order in-flight responses; without this an earlier request can resolve
  // AFTER the final one and clobber the results (same guard as FindingsList).
  const reqSeq = useRef(0);

  const load = useCallback(async (filters) => {
    const myReq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await listMacieBuckets(filters);
      if (myReq !== reqSeq.current) return;
      setBuckets(res?.data?.buckets || res?.data || []);
      setError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setError(e.message);
    } finally {
      if (myReq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Debounced reload + URL sync whenever any filter changes. Rows are cleared
  // up front so the prior query's buckets never linger under the new filters
  // (mirrors FindingsList's setRows([]) on filter change).
  useEffect(() => {
    setBuckets([]);
    const filters = { account, public: publicAccess, shared, encryption, search };
    const t = setTimeout(() => {
      const params = {};
      Object.entries({ account, public: publicAccess, shared, encryption, search }).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      setSearchParams(params, { replace: true });
      load(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [account, publicAccess, shared, encryption, search, load, setSearchParams]);

  const accountLabel = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.accountId] = a.accountName || a.name || a.accountId; });
    return (id) => map[id] || id;
  }, [accounts]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
          title="S3 bucket posture"
          subtitle="Public access, encryption, sharing and sensitivity across every enrolled account"
          color={MACIE_COLOR}
        />
      </motion.div>

      {/* === Filters === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Autocomplete
            size="small"
            options={accounts}
            getOptionLabel={(a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`}
            value={accounts.find((a) => a.accountId === account) || null}
            onChange={(_, v) => setAccount(v?.accountId || '')}
            isOptionEqualToValue={(o, v) => o.accountId === v.accountId}
            renderInput={(params) => <TextField {...params} label="Account" placeholder="All accounts" />}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Public access</InputLabel>
            <Select value={publicAccess} label="Public access" onChange={(e) => setPublicAccess(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {PUBLIC_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Shared access</InputLabel>
            <Select value={shared} label="Shared access" onChange={(e) => setShared(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {SHARED_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o.replace(/_/g, ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Encryption</InputLabel>
            <Select value={encryption} label="Encryption" onChange={(e) => setEncryption(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {ENCRYPTION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{prettyEnc(o)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search buckets"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
        </Stack>
      </Paper>
      </motion.div>

      {/* === Posture table === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<StorageIcon sx={{ fontSize: 20 }} />}
          title="Buckets"
          subtitle="Every S3 bucket Macie has inventoried for the enrolled accounts"
          color={MACIE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${buckets.length} buckets`}
              sx={statusChipSx('info')}
            />
          )}
        />
        {error && <PortalErrorState error={error} sx={{ mb: 2 }} />}
        {loading && buckets.length === 0 ? (
          <PortalLoading label="Loading bucket posture…" color={MACIE_COLOR} />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Bucket</TableCell>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Region</TableCell>
                  <TableCell sx={headCellSx}>Public access</TableCell>
                  <TableCell sx={headCellSx}>Encryption</TableCell>
                  <TableCell sx={headCellSx}>Shared access</TableCell>
                  <TableCell align="right" sx={headCellSx}>Objects</TableCell>
                  <TableCell align="right" sx={headCellSx}>Size</TableCell>
                  <TableCell align="right" sx={headCellSx}>Sensitivity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {buckets.map((b, index) => (
                  <TableRow key={`${b.accountId}/${b.region}/${b.bucketName}`} hover sx={rowSx(index)}>
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{b.bucketName}</TableCell>
                    <TableCell sx={bodyCellSx}>{accountLabel(b.accountId)} ({b.accountId})</TableCell>
                    <TableCell sx={bodyCellSx}>{b.region}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={(b.publicAccess || 'UNKNOWN').replace(/_/g, ' ')} sx={statusChipSx(publicIntent(b.publicAccess))} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={prettyEnc(b.encryptionType)} sx={statusChipSx(encIntent(b.encryptionType))} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip size="small" label={(b.sharedAccess || 'UNKNOWN').replace(/_/g, ' ')} sx={statusChipSx(sharedIntent(b.sharedAccess))} />
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{(b.objectCount ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>{formatBytes(b.sizeBytes)}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {b.sensitivityScore == null || b.sensitivityScore < 0 ? '-' : b.sensitivityScore}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && buckets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<StorageIcon sx={{ fontSize: 28 }} />}
                        title="No buckets match these filters"
                        description="Clear a filter, or run “Refresh buckets” on the dashboard to populate posture."
                        color={MACIE_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
